<template>
  <el-dialog
    :model-value="modelValue"
    title="엑셀 업로드"
    :width="isMobile ? '95%' : '560px'"
    @update:model-value="$emit('update:modelValue', $event)"
    @closed="resetState"
  >
    <!-- 1. 템플릿 다운로드 -->
    <div class="upload-section">
      <p class="section-desc">
        1. 아래 템플릿을 다운로드하여 데이터를 입력하세요.
      </p>
      <el-button @click="downloadTemplate" :loading="downloading">
        템플릿 다운로드
      </el-button>
    </div>

    <el-divider />

    <!-- 2. 파일 선택 -->
    <div class="upload-section">
      <p class="section-desc">
        2. 작성한 엑셀 파일을 선택하세요. (.xlsx)
      </p>
      <el-upload
        ref="uploadRef"
        drag
        :auto-upload="false"
        :limit="1"
        accept=".xlsx,.xls"
        :on-change="handleFileChange"
        :on-remove="handleFileRemove"
      >
        <div class="upload-drag-area">
          <el-icon :size="40"><UploadFilled /></el-icon>
          <div class="el-upload__text">
            파일을 끌어놓거나 <em>클릭하여 선택</em>
          </div>
        </div>
      </el-upload>
    </div>

    <!-- 3. 업로드 결과 -->
    <div v-if="result" class="upload-result">
      <el-divider />
      <el-alert
        v-if="result.success_count > 0"
        :title="`${result.success_count}건 업로드 완료`"
        type="success"
        show-icon
        :closable="false"
        style="margin-bottom: 12px"
      />
      <el-alert
        v-if="result.error_count > 0"
        :title="`${result.error_count}건 오류 발생`"
        type="error"
        show-icon
        :closable="false"
        style="margin-bottom: 12px"
      />
      <el-table
        v-if="result.errors && result.errors.length > 0"
        :data="result.errors"
        size="small"
        max-height="250"
        stripe
      >
        <el-table-column prop="row" label="행" width="60" align="center" />
        <el-table-column prop="field" label="컬럼" width="100" />
        <el-table-column prop="message" label="오류 내용" min-width="200" />
      </el-table>
    </div>

    <template #footer>
      <el-button @click="$emit('update:modelValue', false)">닫기</el-button>
      <el-button
        type="primary"
        :loading="uploading"
        :disabled="!selectedFile || result?.success_count > 0"
        @click="handleUpload"
      >
        업로드
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { UploadFilled } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import axiosInstance from '@/api/axios'

defineProps({
  modelValue: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'uploaded'])

const isMobile = ref(window.innerWidth <= 768)
const updateIsMobile = () => { isMobile.value = window.innerWidth <= 768 }
onMounted(() => window.addEventListener('resize', updateIsMobile))
onUnmounted(() => window.removeEventListener('resize', updateIsMobile))

const uploadRef = ref(null)
const selectedFile = ref(null)
const uploading = ref(false)
const downloading = ref(false)
const result = ref(null)

const handleFileChange = (file) => {
  selectedFile.value = file.raw
}

const handleFileRemove = () => {
  selectedFile.value = null
  result.value = null
}

const resetState = () => {
  selectedFile.value = null
  uploading.value = false
  result.value = null
  uploadRef.value?.clearFiles()
}

const downloadTemplate = async () => {
  downloading.value = true
  try {
    const response = await axiosInstance.get('/condolences/template/', {
      responseType: 'blob',
    })
    const url = window.URL.createObjectURL(response.data)
    const link = document.createElement('a')
    link.href = url
    link.download = 'condolence_template.xlsx'
    link.click()
    window.URL.revokeObjectURL(url)
  } catch (error) {
    ElMessage.error('템플릿 다운로드에 실패했습니다.')
  } finally {
    downloading.value = false
  }
}

const handleUpload = async () => {
  if (!selectedFile.value) return

  uploading.value = true
  result.value = null

  const formData = new FormData()
  formData.append('file', selectedFile.value)

  try {
    const response = await axiosInstance.post('/condolences/upload/', formData)
    result.value = response.data
    ElMessage.success(`${response.data.success_count}건 업로드 완료`)
    emit('uploaded')
  } catch (error) {
    const data = error.response?.data
    if (data?.errors) {
      result.value = data
    } else {
      ElMessage.error(data?.detail || '업로드에 실패했습니다.')
    }
  } finally {
    uploading.value = false
  }
}
</script>

<style scoped>
.upload-section {
  margin-bottom: 8px;
}

.section-desc {
  margin: 0 0 8px 0;
  color: #606266;
  font-size: 14px;
}

.upload-drag-area {
  padding: 20px 0;
  text-align: center;
  color: #909399;
}

.upload-result {
  margin-top: 4px;
}
</style>
